import { z } from "zod"

import type { AgentType, RefCast } from "./connector-tool-policy-cast"
import type { TemplatedJson } from "./connector-tool-policy-json"
import { isAgentType, REF_CASTS } from "./connector-tool-policy-cast"
import {
	TEMPLATE_TOKEN_SOURCE,
	templatedJsonValue,
	templateTokenBodies,
	templateTokenRegex,
	templateTokenSpans,
	tokensInTemplatedJson,
	visitJsonStrings,
} from "./connector-tool-policy-json"
import {
	findShadowedPaths,
	formatFieldPath,
	hasWildcardSegment,
	parseFieldPath,
} from "./connector-tool-policy-path"

export const MAX_POLICY_FIELDS = 64
export const MAX_ENUM_VALUES = 50
export const MAX_FIXED_VALUE_BYTES = 1_000
export const MAX_TEMPLATE_LENGTH = 1_024
export const MAX_REFS_PER_CALL = 16
export const MAX_FALLBACK_STEPS = 4
export const MAX_POLICY_VARIANTS_PER_TOOL = 6
export const MAX_VARIANT_LABEL_CHARS = 40
export const MAX_VARIANT_DESCRIPTION_CHARS = 280
export const MAX_AGENT_PATTERN_LENGTH = 200

const REF_KEY = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/
const SECTION_ID = /^[A-Za-z0-9-]{8,64}$/
const VARIANT_ID = /^[a-z0-9][a-z0-9-]{0,31}$/

/** The segment that marks an id a third-party system owns. */
export const EXTERNAL_IDS_SEGMENT = "externalIds"

export const RECORD_SCOPES = ["contact", "company"] as const
export type RecordScope = (typeof RECORD_SCOPES)[number]

// Closed catalogues: a record ref names a column, so anything outside these is a
// typo rather than data we could ever resolve.
export const RECORD_FIELDS: Record<RecordScope, readonly string[]> = {
	contact: ["email", "name"],
	company: ["name", "domain", EXTERNAL_IDS_SEGMENT],
}

export const RECORD_EXTERNAL_IDS: Record<RecordScope, readonly string[]> = {
	contact: ["intercom", "hubspot", "productboard", "attio", "zendesk"],
	company: ["intercom", "hubspot", "productboard", "attio", "linear"],
}

export type SecureRef =
	| { namespace: "widget"; key: string }
	| { namespace: "http"; sectionId: string | null; key: string }
	| {
			namespace: RecordScope
			/** Set by `<scope>.externalIds.<key>`, null for a plain field. */
			externalId: string | null
			/** Set by `<scope>.<field>`, null when externalId is set. */
			field: string | null
	  }

function parseRecordRef(scope: RecordScope, parts: string[]): SecureRef | null {
	if (parts.length === 2) {
		const field = parts[1]!
		if (!RECORD_FIELDS[scope].includes(field)) return null
		return { namespace: scope, externalId: null, field }
	}
	if (parts.length === 3 && parts[1] === EXTERNAL_IDS_SEGMENT) {
		const key = parts[2]!
		if (!RECORD_EXTERNAL_IDS[scope].includes(key)) return null
		return { namespace: scope, externalId: key, field: null }
	}
	return null
}

export function parseSecureRef(raw: string): SecureRef | null {
	const parts = raw.split(".")
	if (parts[0] === "widget") {
		if (parts.length !== 2 || !REF_KEY.test(parts[1]!)) return null
		return { namespace: "widget", key: parts[1]! }
	}
	if (parts[0] === "contact" || parts[0] === "company") {
		return parseRecordRef(parts[0], parts)
	}
	if (parts[0] !== "http") return null
	if (parts.length === 2 && REF_KEY.test(parts[1]!)) {
		return { namespace: "http", sectionId: null, key: parts[1]! }
	}
	if (
		parts.length === 3 &&
		SECTION_ID.test(parts[1]!) &&
		REF_KEY.test(parts[2]!)
	) {
		return { namespace: "http", sectionId: parts[1]!, key: parts[2]! }
	}
	return null
}

export function formatSecureRef(ref: SecureRef): string {
	if (ref.namespace === "widget") return `widget.${ref.key}`
	if (ref.namespace === "http") {
		return ref.sectionId
			? `http.${ref.sectionId}.${ref.key}`
			: `http.${ref.key}`
	}
	return ref.externalId
		? `${ref.namespace}.${EXTERNAL_IDS_SEGMENT}.${ref.externalId}`
		: `${ref.namespace}.${ref.field}`
}

/** The token head that names the model's own value for the field. */
export const AGENT_TOKEN = "agent"

export type TemplateToken =
	| { kind: "ref"; ref: string }
	/** `path` descends from the rule's own field, so a rule reads only its subtree. */
	| { kind: "agent"; path?: string; type?: AgentType; pattern?: string }

function hasNestedQuantifier(pattern: string): boolean {
	// Marks, per open group, whether its body carries a quantifier; a quantifier
	// applied right after such a group closes is the catastrophic shape.
	const stack: boolean[] = []
	let closedGroupHadQuantifier: boolean | null = null
	for (let pos = 0; pos < pattern.length; pos++) {
		const char = pattern[pos]!
		if (char === "\\") {
			pos += 1
			closedGroupHadQuantifier = null
			continue
		}
		if (char === "[") {
			while (pos < pattern.length && pattern[pos] !== "]") {
				if (pattern[pos] === "\\") pos += 1
				pos += 1
			}
			closedGroupHadQuantifier = null
			continue
		}
		if (char === "(") {
			stack.push(false)
			closedGroupHadQuantifier = null
			continue
		}
		if (char === ")") {
			closedGroupHadQuantifier = stack.pop() ?? false
			if (closedGroupHadQuantifier && stack.length > 0) {
				stack[stack.length - 1] = true
			}
			continue
		}
		if (char === "*" || char === "+" || char === "?") {
			if (closedGroupHadQuantifier) return true
			for (let depth = 0; depth < stack.length; depth++) stack[depth] = true
			closedGroupHadQuantifier = null
			continue
		}
		closedGroupHadQuantifier = null
	}
	return false
}

/**
 * A conservative subset with linear-time matching in practice: no lookaround, no
 * backreferences, no quantified group whose body is itself quantified.
 */
export function isSafeAgentPattern(pattern: string): boolean {
	if (!pattern || pattern.length > MAX_AGENT_PATTERN_LENGTH) return false
	if (/\(\?<?[=!]/.test(pattern)) return false
	if (/\\(?:[1-9]|k)/.test(pattern)) return false
	if (hasNestedQuantifier(pattern)) return false
	try {
		new RegExp(`^(?:${pattern})$`, "u")
	} catch {
		return false
	}
	return true
}

/** The anchored matcher for an agent constraint, or null for an unsafe pattern. */
export function agentPatternRegex(pattern: string): RegExp | null {
	if (!isSafeAgentPattern(pattern)) return null
	return new RegExp(`^(?:${pattern})$`, "u")
}

const AGENT_HEAD = /^agent(?:\.(.+))?$/

/**
 * Splits a trailing type keyword off the raw path text. It has to happen before
 * parseFieldPath, because formatFieldPath emits a bare-safe key unquoted and would
 * make `["integer"]` indistinguishable from `integer`.
 */
function splitAgentType(path: string): { path?: string; type?: AgentType } {
	const dot = path.lastIndexOf(".")
	const tail = dot === -1 ? path : path.slice(dot + 1)
	if (!isAgentType(tail)) return { path }
	return dot === -1 ? { type: tail } : { path: path.slice(0, dot), type: tail }
}

/** `agent`, `agent.<path>`, or either with a trailing `.<type>` that casts it. */
function parseAgentHead(
	head: string,
): Extract<TemplateToken, { kind: "agent" }> | null {
	const match = AGENT_HEAD.exec(head)
	if (!match) return null
	if (match[1] === undefined) return { kind: "agent" }

	const { path, type } = splitAgentType(match[1])
	if (path === undefined) return { kind: "agent", type }
	const segments = parseFieldPath(path)
	// A wildcard names many sites, so no single value exists to read.
	if (!segments || hasWildcardSegment(segments)) return null
	return {
		kind: "agent",
		path: formatFieldPath(segments),
		...(type && { type }),
	}
}

export function parseTemplateToken(body: string): TemplateToken | null {
	const pipe = body.indexOf("|")
	if (pipe === -1) {
		const agent = parseAgentHead(body)
		if (agent) return agent
		return parseSecureRef(body) ? { kind: "ref", ref: body } : null
	}
	// A pipe is only meaningful after `agent`; secure refs stay pipe-free.
	const agent = parseAgentHead(body.slice(0, pipe).trim())
	if (!agent) return null
	// A regex over a container or a null names nothing it could match.
	if (agent.type === "json" || agent.type === "null") return null
	const pattern = body.slice(pipe + 1).trim()
	if (!isSafeAgentPattern(pattern)) return null
	return { ...agent, pattern }
}

/** The single token body when the text is exactly one `{{token}}`, else null. */
export function wholeTemplateToken(text: string): string | null {
	const match = new RegExp(`^${TEMPLATE_TOKEN_SOURCE}$`).exec(text)
	return match?.[1] ?? null
}

/** The type a text resolves to whole, or undefined when it is not one typed token. */
export function wholeTemplateType(text: string): AgentType | undefined {
	const body = wholeTemplateToken(text)
	if (body === null) return undefined
	const token = parseTemplateToken(body)
	return token?.kind === "agent" ? token.type : undefined
}

function isTypedToken(body: string): boolean {
	const token = parseTemplateToken(body)
	return token?.kind === "agent" && token.type !== undefined
}

/**
 * Whether a typed token owns the slot it sits in, which is what its type needs to
 * survive: the whole text, or, in a JSON document, a whole string leaf.
 */
function ownsItsSlot(
	text: string,
	span: { start: number; end: number },
	jsonText: boolean,
): boolean {
	const before = text.slice(0, span.start)
	const after = text.slice(span.end)
	if (!before.trim() && !after.trim()) return true
	return jsonText && before.endsWith('"') && after.startsWith('"')
}

/**
 * Typed bodies that do not own their slot. Anywhere else the token renders into
 * surrounding text, which is a string however the value started out.
 */
export function misplacedTypedTokens(
	step: ChainStep,
	cast?: RefCast,
): string[] {
	const bad: string[] = []
	const flag = (body: string) => {
		if (isTypedToken(body) && !bad.includes(body)) bad.push(body)
	}

	if (step.mode === "template") {
		// A json cast parses the rendered text, so its string leaves are real slots.
		const jsonText = cast === "json"
		for (const span of templateTokenSpans(step.template)) {
			if (!ownsItsSlot(step.template, span, jsonText)) flag(span.body)
		}
	}

	if (step.mode === "json") {
		visitJsonStrings(step.value, (text) => {
			if (wholeTemplateToken(text) !== null) return
			for (const body of templateTokenBodies(text)) flag(body)
		})
	}

	return bad
}

// A JSON scalar, so a stub is valid both on its own and inside a string slot.
const JSON_TEMPLATE_STUB = "0"

/**
 * Whether a cast:"json" template can parse once its tokens carry values. Only the
 * template's own punctuation is checked: what a token resolves to is unknown here.
 */
export function jsonTemplateParses(template: string): boolean {
	// One whole token renders the document itself, so there is no shape to check.
	if (wholeTemplateToken(template) !== null) return true
	try {
		JSON.parse(template.replace(templateTokenRegex(), JSON_TEMPLATE_STUB))
		return true
	} catch {
		return false
	}
}

/** The body a token round-trips to, bracket-quoting a key that reads as a type. */
export function formatAgentToken(
	token: Extract<TemplateToken, { kind: "agent" }>,
): string {
	let head = AGENT_TOKEN
	if (token.path) {
		const dot = token.path.lastIndexOf(".")
		const tail = token.path.slice(dot + 1)
		const quoted =
			isAgentType(tail) && !tail.startsWith("[")
				? `${token.path.slice(0, dot + 1)}["${tail}"]`
				: token.path
		head += `.${quoted}`
	}
	if (token.type) head += `.${token.type}`
	return token.pattern ? `${head} | ${token.pattern}` : head
}

/** Every token in order, or null when any body is invalid. */
export function parseTemplateTokens(template: string): TemplateToken[] | null {
	const tokens: TemplateToken[] = []
	for (const body of templateTokenBodies(template)) {
		const token = parseTemplateToken(body)
		if (!token) return null
		tokens.push(token)
	}
	return tokens
}

export function templateHasAgentToken(template: string): boolean {
	return (parseTemplateTokens(template) ?? []).some(
		(token) => token.kind === "agent",
	)
}

/** The distinct secure-ref bodies, skipping agent tokens; null on an invalid body. */
export function parseTemplateRefs(template: string): string[] | null {
	const tokens = parseTemplateTokens(template)
	if (!tokens) return null
	const found: string[] = []
	for (const token of tokens) {
		if (token.kind !== "ref") continue
		if (!found.includes(token.ref)) found.push(token.ref)
	}
	return found
}

const RefString = z.string().refine((value) => !!parseSecureRef(value), {
	message: "must be widget.<key>, http.<key>, or http.<connectorId>.<key>",
})

const EnumValue = z.union([z.string().max(512), z.number(), z.boolean()])
export type EnumValue = z.infer<typeof EnumValue>

// Kept to JSON scalars and a flat list: a fixed value has to survive Zero's sync
// types, and a nested constant is a sign the rule belongs deeper in the path.
const FixedValue = z.union([
	z.string().max(MAX_FIXED_VALUE_BYTES),
	z.number().finite(),
	z.boolean(),
	z.null(),
	z.array(EnumValue).max(MAX_ENUM_VALUES),
])
export type FixedValue = z.infer<typeof FixedValue>

const TemplateString = z
	.string()
	.min(1)
	.max(MAX_TEMPLATE_LENGTH)
	.refine((value) => !!parseTemplateTokens(value), {
		message: "contains a token that is not a valid reference",
	})
	.refine((value) => (parseTemplateTokens(value) ?? []).length > 0, {
		message: "must contain at least one {{reference}} or {{agent}}",
	})

const FallbackStep = z.discriminatedUnion("mode", [
	z.object({ mode: z.literal("ref"), ref: RefString }),
	z.object({ mode: z.literal("template"), template: TemplateString }),
	// Narrower than FixedValue on purpose: every other step resolves to a string,
	// so a non-string terminator would make the injected type depend on the winner.
	z.object({
		mode: z.literal("fixed"),
		value: z.string().max(MAX_FIXED_VALUE_BYTES),
	}),
])
export type FallbackStep = z.infer<typeof FallbackStep>

const Fallbacks = z
	.array(FallbackStep)
	.min(1)
	.max(MAX_FALLBACK_STEPS)
	.superRefine((steps, ctx) => {
		steps.forEach((step, index) => {
			if (step.mode === "fixed" && index !== steps.length - 1) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [index],
					message: "a fixed value ends the chain, so nothing may follow it",
				})
			}
		})
	})

/** A pinned object or list. Its string leaves may carry `{{reference}}` tokens. */
export const StructuredValue = templatedJsonValue({
	isValidLeaf: (text) => parseTemplateTokens(text) !== null,
})

const RefCastSchema = z.enum(REF_CASTS)

export const FieldRule = z.discriminatedUnion("mode", [
	z.object({ mode: z.literal("any") }),
	z.object({ mode: z.literal("blocked") }),
	z.object({ mode: z.literal("fixed"), value: FixedValue }),
	// Separate from fixed so a stored fixed value never gains reference semantics,
	// and so the string-only chain terminator keeps its meaning.
	z.object({ mode: z.literal("json"), value: StructuredValue }),
	z.object({
		mode: z.literal("ref"),
		ref: RefString,
		// One cast for the whole chain: the injected type must not depend on which
		// step won.
		cast: RefCastSchema.optional(),
		fallbacks: Fallbacks.optional(),
	}),
	z.object({
		mode: z.literal("enum"),
		values: z.array(EnumValue).min(1).max(MAX_ENUM_VALUES),
	}),
	z.object({
		mode: z.literal("template"),
		template: TemplateString,
		cast: RefCastSchema.optional(),
		fallbacks: Fallbacks.optional(),
	}),
])
export type FieldRule = z.infer<typeof FieldRule>

export type FieldRuleMode = FieldRule["mode"]

/** Modes whose value the platform supplies, so the model must never send one. */
export function isInjectedMode(mode: FieldRuleMode): boolean {
	return (
		mode === "fixed" || mode === "json" || mode === "ref" || mode === "template"
	)
}

/** A rule that decides its whole subtree, so any deeper rule is unreachable. */
export function isTerminalMode(mode: FieldRuleMode): boolean {
	return isInjectedMode(mode) || mode === "blocked"
}

const VariantFields = z.record(FieldRule)

function checkFields(
	fields: Record<string, FieldRule>,
	ctx: z.RefinementCtx,
	prefix: (string | number)[],
) {
	const paths = Object.keys(fields)
	if (paths.length > MAX_POLICY_FIELDS) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: prefix,
			message: `at most ${MAX_POLICY_FIELDS} fields per tool`,
		})
	}
	for (const path of paths) {
		if (!parseFieldPath(path)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: [...prefix, "fields", path],
				message: `"${path}" is not a valid field path`,
			})
		}
	}
}

/**
 * Write-path only. A pinned container decides its whole subtree, so a rule below
 * one can never apply and is almost always a mistake the editor should surface.
 */
function checkConflicts(
	fields: Record<string, FieldRule>,
	ctx: z.RefinementCtx,
	prefix: (string | number)[],
) {
	const conflicts = findShadowedPaths(fields, (rule) =>
		isTerminalMode(rule.mode),
	)
	for (const conflict of conflicts) {
		// A blocked ancestor over blocked descendants is reachable in today's editor,
		// so it is pruned at apply time rather than refused here.
		const shadowing = fields[conflict.shadowedBy]
		if (!shadowing || !isInjectedMode(shadowing.mode)) continue
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: [...prefix, "fields", conflict.path],
			message: `"${conflict.shadowedBy}" is set by the workspace, so "${conflict.path}" can never apply`,
		})
	}
}

export const ToolPolicyVariant = z.object({
	id: z
		.string()
		.regex(VARIANT_ID, "must be lowercase letters, digits or dashes"),
	label: z.string().min(1).max(MAX_VARIANT_LABEL_CHARS),
	description: z.string().max(MAX_VARIANT_DESCRIPTION_CHARS).optional(),
	unknownFields: z.enum(["allow", "block"]).default("allow"),
	fields: VariantFields.default({}),
})
export type ToolPolicyVariant = z.infer<typeof ToolPolicyVariant>

const ToolPolicyShape = z
	.object({
		unknownFields: z.enum(["allow", "block"]).default("allow"),
		fields: VariantFields.default({}),
		/** Names the base variant in the settings tabs and in the agent's catalog. */
		label: z.string().max(MAX_VARIANT_LABEL_CHARS).optional(),
		description: z.string().max(MAX_VARIANT_DESCRIPTION_CHARS).optional(),
		// Optional and additive on purpose: parseToolPolicies falls back to {} on a
		// failed parse, so a shape older code rejects would drop every guard.
		variants: z.array(ToolPolicyVariant).optional(),
	})
	.superRefine((policy, ctx) => {
		checkFields(policy.fields, ctx, [])

		const variants = policy.variants ?? []
		if (variants.length + 1 > MAX_POLICY_VARIANTS_PER_TOOL) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["variants"],
				message: `at most ${MAX_POLICY_VARIANTS_PER_TOOL} variants per tool`,
			})
		}

		const seen = new Set<string>()
		variants.forEach((variant, index) => {
			if (seen.has(variant.id)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["variants", index, "id"],
					message: `"${variant.id}" is already used by another variant`,
				})
			}
			seen.add(variant.id)
			checkFields(variant.fields, ctx, ["variants", index])
		})
	})

// Write-path only: a wildcard names many input sites, each with its own agent
// value, so no single {{agent}} substitution exists. Apply time re-checks stored
// policies, so the read path may stay lenient.
function checkAgentPaths(
	fields: Record<string, FieldRule>,
	ctx: z.RefinementCtx,
	prefix: (string | number)[],
) {
	for (const [path, rule] of Object.entries(fields)) {
		if (!ruleUsesAgentInput(rule)) continue
		const segments = parseFieldPath(path)
		if (segments && hasWildcardSegment(segments)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: [...prefix, "fields", path],
				message: "{{agent}} cannot be used on a wildcard path",
			})
		}
	}
}

// Write-path only, for the same reason as checkAgentPaths: a typed token only
// carries its type when it owns its slot.
function checkTypedTokenPlacement(
	fields: Record<string, FieldRule>,
	ctx: z.RefinementCtx,
	prefix: (string | number)[],
) {
	for (const [path, rule] of Object.entries(fields)) {
		const cast = fieldRuleCast(rule)
		for (const step of fieldRuleSteps(rule)) {
			const [misplaced] = misplacedTypedTokens(step, cast)
			if (misplaced) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: [...prefix, "fields", path],
					message: `{{${misplaced}}} sets the value's type, so it has to be the whole value rather than sit inside text`,
				})
			}
		}
	}
}

// Write-path only: a template that cannot parse refuses every call at apply time,
// with nothing in the refusal the model could act on.
function checkJsonTemplates(
	fields: Record<string, FieldRule>,
	ctx: z.RefinementCtx,
	prefix: (string | number)[],
) {
	for (const [path, rule] of Object.entries(fields)) {
		if (fieldRuleCast(rule) !== "json") continue
		for (const step of fieldRuleSteps(rule)) {
			if (step.mode !== "template" || jsonTemplateParses(step.template))
				continue
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: [...prefix, "fields", path],
				message: `"${path}" writes JSON, so its template has to parse as JSON once its tokens carry values`,
			})
		}
	}
}

/**
 * The write path. A conflict is refused here so the editor cannot save one, but
 * never on the read path, where a rejected policy would leave the tool unguarded.
 */
export const ToolPolicy = ToolPolicyShape.superRefine((policy, ctx) => {
	checkConflicts(policy.fields, ctx, [])
	checkAgentPaths(policy.fields, ctx, [])
	checkTypedTokenPlacement(policy.fields, ctx, [])
	checkJsonTemplates(policy.fields, ctx, [])
	;(policy.variants ?? []).forEach((variant, index) => {
		checkConflicts(variant.fields, ctx, ["variants", index])
		checkAgentPaths(variant.fields, ctx, ["variants", index])
		checkTypedTokenPlacement(variant.fields, ctx, ["variants", index])
		checkJsonTemplates(variant.fields, ctx, ["variants", index])
	})
})
export type ToolPolicy = z.infer<typeof ToolPolicy>

export const ToolPolicies = z.record(ToolPolicy)

const StoredToolPolicies = z.record(ToolPolicyShape)
export type ToolPolicies = z.infer<typeof ToolPolicies>

export function emptyToolPolicy(): ToolPolicy {
	return { unknownFields: "allow", fields: {} }
}

/** One callable configuration of a tool. The base variant carries the empty id. */
export interface ResolvedToolPolicyVariant {
	id: string
	label: string
	description?: string
	unknownFields: "allow" | "block"
	fields: Record<string, FieldRule>
}

// A variant with no rules and no unknown-field block enforces nothing, so it reads
// as unguarded everywhere rather than as an empty guard.
function isGuardingVariant(variant: {
	unknownFields: "allow" | "block"
	fields: Record<string, FieldRule>
}): boolean {
	return (
		variant.unknownFields === "block" || Object.keys(variant.fields).length > 0
	)
}

export function isGuardingPolicy(policy: ToolPolicy | null): boolean {
	if (!policy) return false
	if (isGuardingVariant(policy)) return true
	return (policy.variants ?? []).some(isGuardingVariant)
}

/** Base variant first, then the extra ones, in stored order. */
export function resolveToolPolicyVariants(
	policy: ToolPolicy,
	toolName: string,
): ResolvedToolPolicyVariant[] {
	return [
		{
			id: "",
			label: policy.label?.trim() || toolName,
			...(policy.description && { description: policy.description }),
			unknownFields: policy.unknownFields,
			fields: policy.fields,
		},
		...(policy.variants ?? []).map((variant) => ({
			id: variant.id,
			label: variant.label,
			...(variant.description && { description: variant.description }),
			unknownFields: variant.unknownFields,
			fields: variant.fields,
		})),
	]
}

export function parseToolPolicies(raw: unknown): ToolPolicies {
	if (!raw || typeof raw !== "object") return {}
	// Deliberately the shape schema, not ToolPolicy: a conflict the write path
	// refuses must not fall back to {} here and leave the tool unguarded.
	const parsed = StoredToolPolicies.safeParse(raw)
	return parsed.success ? parsed.data : {}
}

export function policyForTool(
	raw: unknown,
	toolName: string,
): ToolPolicy | null {
	const policies = parseToolPolicies(raw)
	const policy = policies[toolName] ?? null
	return isGuardingPolicy(policy) ? policy : null
}

/** Every variant of a tool, guarding or not. Empty when the tool has no policy. */
export function variantsForTool(
	raw: unknown,
	toolName: string,
): ResolvedToolPolicyVariant[] {
	const policy = parseToolPolicies(raw)[toolName]
	if (!policy) return []
	return resolveToolPolicyVariants(policy, toolName)
}

/** The variant a call runs under, or null when that variant enforces nothing. */
export function policyForVariant(
	raw: unknown,
	toolName: string,
	variantId: string | null,
): ResolvedToolPolicyVariant | null {
	const variant =
		variantsForTool(raw, toolName).find(
			(candidate) => candidate.id === (variantId ?? ""),
		) ?? null
	if (!variant) return null
	return isGuardingVariant(variant) ? variant : null
}

export function isVariantId(value: string): boolean {
	return VARIANT_ID.test(value)
}

/** A stable id slugged from the label, deduped against the ids already in use. */
export function variantIdFromLabel(
	label: string,
	taken: readonly string[],
): string {
	const slug = label
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 32)
		.replace(/-+$/, "")
	const base = isVariantId(slug) ? slug : "variant"
	if (!taken.includes(base)) return base
	for (let suffix = 2; ; suffix += 1) {
		const candidate = `${base.slice(0, 29)}-${suffix}`
		if (!taken.includes(candidate)) return candidate
	}
}

export type ChainStep =
	| { mode: "ref"; ref: string }
	| { mode: "template"; template: string }
	| { mode: "fixed"; value: FixedValue }
	| { mode: "json"; value: TemplatedJson }

/** The cast the winning step's resolved string is coerced through, if any. */
export function fieldRuleCast(rule: FieldRule): RefCast | undefined {
	return rule.mode === "ref" || rule.mode === "template" ? rule.cast : undefined
}

/**
 * The ordered chain a rule injects from: its own value first, then its fallbacks.
 * The resolver and the ref budget both read this, so neither can drift.
 */
export function fieldRuleSteps(rule: FieldRule): ChainStep[] {
	switch (rule.mode) {
		case "fixed":
			return [{ mode: "fixed", value: rule.value }]
		case "json":
			return [{ mode: "json", value: rule.value }]
		case "ref":
			return [{ mode: "ref", ref: rule.ref }, ...(rule.fallbacks ?? [])]
		case "template":
			return [
				{ mode: "template", template: rule.template },
				...(rule.fallbacks ?? []),
			]
		case "any":
		case "blocked":
		case "enum":
			return []
	}
}

export function refsInStep(step: ChainStep): string[] {
	if (step.mode === "ref") return [step.ref]
	if (step.mode === "template") return parseTemplateRefs(step.template) ?? []
	if (step.mode === "json") {
		return tokensInTemplatedJson(step.value).filter(
			(body) => parseTemplateToken(body)?.kind === "ref",
		)
	}
	return []
}

/** Every token body a step carries, in first-seen order. */
export function tokenBodiesInStep(step: ChainStep): string[] {
	if (step.mode === "template") {
		const bodies: string[] = []
		for (const body of templateTokenBodies(step.template)) {
			if (!bodies.includes(body)) bodies.push(body)
		}
		return bodies
	}
	if (step.mode === "json") return tokensInTemplatedJson(step.value)
	return []
}

export interface AgentTokenUse {
	/** The token body as written, which is the key the renderer looks up. */
	body: string
	path?: string
	type?: AgentType
	pattern?: string
}

export function agentTokensInStep(step: ChainStep): AgentTokenUse[] {
	const uses: AgentTokenUse[] = []
	for (const body of tokenBodiesInStep(step)) {
		const token = parseTemplateToken(body)
		if (token?.kind !== "agent") continue
		uses.push({
			body,
			...(token.path && { path: token.path }),
			...(token.type && { type: token.type }),
			...(token.pattern && { pattern: token.pattern }),
		})
	}
	return uses
}

export function stepUsesAgentInput(step: ChainStep): boolean {
	return agentTokensInStep(step).length > 0
}

/**
 * The sub-paths a rule reads out of what the model sent, or null when a bare
 * `{{agent}}` takes the whole value.
 */
export function agentReadPaths(rule: FieldRule): string[] | null {
	const paths: string[] = []
	for (const step of fieldRuleSteps(rule)) {
		for (const use of agentTokensInStep(step)) {
			if (!use.path) return null
			if (!paths.includes(use.path)) paths.push(use.path)
		}
	}
	return paths
}

/** The field keeps its own input: the model's value feeds the rule's tokens. */
export function ruleUsesAgentInput(rule: FieldRule): boolean {
	return fieldRuleSteps(rule).some(stepUsesAgentInput)
}

/** Every ref one variant could reach, counting all steps of every chain. */
export function refsInPolicy(variant: {
	fields: Record<string, FieldRule>
	// Unread here, but callers pass a whole policy variant.
	unknownFields?: "allow" | "block"
}): string[] {
	const refs: string[] = []
	for (const rule of Object.values(variant.fields)) {
		for (const step of fieldRuleSteps(rule)) {
			for (const ref of refsInStep(step)) {
				if (!refs.includes(ref)) refs.push(ref)
			}
		}
	}
	return refs
}
